import {
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  Flex,
  Heading,
  Link,
  Stack,
  Text,
  Image,
} from "@chakra-ui/react";
import { useRouter } from "next/router";
import Analytics from "../analytics";
import { Section } from "../section";
import { ProductHuntBadge } from "../product-hunt-badge";
import { AiFillStar } from "react-icons/ai";
import { NewsletterSignup } from "../newsletter-signup";
import BetaPassword from "../beta-password";
import { useConversionTracker } from "../../hooks/use-conversion-tracker";
import { isChina } from "../../utils/is-china";
import { localeFromLangCode } from "../../utils/locale-from-header";
import FacebookPixel from "../facebook-pixel";
import TiktokPixel from "../tiktok-pixel";
import { useMixpanel } from "../../hooks/use-mixpanel";
import Script from "next/script";
import { siteConfig } from "../../configuration/config";
import { LandingLocale } from "../../localization/landing";
import { NavigationLanding } from "./navigation-landing";
import FooterLanding from "./footer-landing";

interface LayoutLanding {
  children: React.ReactNode;
  l: LandingLocale;
  baseLangCode: string;
}

export default function LayoutLanding({ children, l: defaultL, baseLangCode }: LayoutLanding) {
  const l = defaultL || localeFromLangCode("en-US").l;
  const router = useRouter();

  const isDefaultEnglish = !baseLangCode || baseLangCode === "en";
  const isFrontpageOrBilling =
    router.pathname === "/" ||
    router.pathname.startsWith("/billing") ||
    router.pathname.startsWith("/authentication") ||
    router.pathname.startsWith("/campaign") ||
    router.pathname.startsWith("/invitation") ||
    router.pathname.startsWith("/labs");

  const isQuiz = router.pathname.startsWith("/quiz");
  const isPartners = router.pathname.startsWith("/partners");
  const isBilling = router.pathname === "/billing";
  const isAccessRequest = router.pathname === "/authentication/access-request";
  const showTopNav = !isBilling && !isAccessRequest && !router.pathname.startsWith("/campaign");

  useConversionTracker();
  useMixpanel(true);

  return (
    <Box className={siteConfig.overrideLanding ? "partner-landing" : ""}>
      <BetaPassword />

      {siteConfig.isDefault && (
        <>
          <Script
            id="CookieConsent"
            src="https://policy.app.cookieinformation.com/uc.js"
            data-culture="EN"
            data-gcm-version="2.0"
            type="text/javascript"
          ></Script>

          <Analytics gKey={"G-LG4J2SKNZW"} />

          <link rel="preconnect" href="https://dev.visualwebsiteoptimizer.com" />
          <Script id="vwoCode" strategy="beforeInteractive">
            {`
window._vwo_code || (function() {
var account_id=836822,
version=2.1,
settings_tolerance=2000,
hide_element='body',
hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important;transition:none !important;',
/* DO NOT EDIT BELOW THIS LINE */
f=false,w=window,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}var stT=cc.stT==='session'?w.sessionStorage:w.localStorage;code={nonce:v&&v.nonce,use_existing_jquery:function(){return typeof use_existing_jquery!=='undefined'?use_existing_jquery:undefined},library_tolerance:function(){return typeof library_tolerance!=='undefined'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return'{'+(cc.hES||hide_element_style)+'}'},hide_element:function(){if(performance.getEntriesByName('first-contentful-paint')[0]){return''}return typeof cc.hE==='string'?cc.hE:hide_element},getVersion:function(){return version},finish:function(e){if(!f){f=true;var t=d.getElementById('_vis_opt_path_hides');if(t)t.parentNode.removeChild(t);if(e)(new Image).src='https://dev.visualwebsiteoptimizer.com/ee.gif?a='+account_id+e}},finished:function(){return f},addScript:function(e){var t=d.createElement('script');t.type='text/javascript';if(e.src){t.src=e.src}else{t.text=e.text}v&&t.setAttribute('nonce',v.nonce);d.getElementsByTagName('head')[0].appendChild(t)},load:function(e,t){var n=this.getSettings(),i=d.createElement('script'),r=this;t=t||{};if(n){i.textContent=n;d.getElementsByTagName('head')[0].appendChild(i);if(!w.VWO||VWO.caE){stT.removeItem(cK);r.load(e)}}else{var o=new XMLHttpRequest;o.open('GET',e,true);o.withCredentials=!t.dSC;o.responseType=t.responseType||'text';o.onload=function(){if(t.onloadCb){return t.onloadCb(o,e)}if(o.status===200||o.status===304){w._vwo_code.addScript({text:o.responseText})}else{w._vwo_code.finish('&e=loading_failure:'+e)}};o.onerror=function(){if(t.onerrorCb){return t.onerrorCb(e)}w._vwo_code.finish('&e=loading_failure:'+e)};o.send()}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;var e=this.settings_tolerance();w._vwo_settings_timer=setTimeout(function(){w._vwo_code.finish();stT.removeItem(cK)},e);var t;if(this.hide_element()!=='body'){t=d.createElement('style');var n=this.hide_element(),i=n?n+this.hide_element_style():'',r=d.getElementsByTagName('head')[0];t.setAttribute('id','_vis_opt_path_hides');v&&t.setAttribute('nonce',v.nonce);t.setAttribute('type','text/css');if(t.styleSheet)t.styleSheet.cssText=i;else t.appendChild(d.createTextNode(i));r.appendChild(t)}else{t=d.getElementsByTagName('head')[0];var i=d.createElement('div');i.style.cssText='z-index: 2147483647 !important;position: fixed !important;left: 0 !important;top: 0 !important;width: 100% !important;height: 100% !important;background: white !important;';i.setAttribute('id','_vis_opt_path_hides');i.classList.add('_vis_hide_layer');t.parentNode.insertBefore(i,t.nextSibling)}var o=window._vis_opt_url||d.URL,s='https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(o)+'&vn='+version;if(w.location.search.indexOf('_vwo_xhr')!==-1){this.addScript({src:s})}else{this.load(s+'&x=true')}}};w._vwo_code=code;code.init();})();(function(){var i=window;function t(){if(i._vwo_code){var e=t.hidingStyle=document.getElementById('_vis_opt_path_hides')||t.hidingStyle;if(!i._vwo_code.finished()&&!_vwo_code.libExecuted&&(!i.VWO||!VWO.dNR)){if(!document.getElementById('_vis_opt_path_hides')){document.getElementsByTagName('head')[0].appendChild(e)}requestAnimationFrame(t)}}}t()})();
            `}
          </Script>

          {router.pathname !== "/" && (
            <>
              <FacebookPixel />
              <TiktokPixel />
            </>
          )}
        </>
      )}

      <Box
        w={"100%"}
        maxW="1100px"
        margin="0 auto"
        display={{ base: "none", md: "block" }}
        id="service-bar"
      >
        <Flex py={1} px={3}>
          <Text fontSize="xs">{l["layout.serviceBar.text"]}</Text>

          <Flex ml={10}>
            <Flex>
              {[0, 1, 2, 3, 4].map((k) => (
                <AiFillStar
                  key={k}
                  color="var(--chakra-colors-brand-green-500)"
                  fontSize="16px"
                  style={{ marginTop: "1px" }}
                />
              ))}
              <Text fontSize="xs" ml={1}>
                {l["layout.serviceBar.community"]}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Box>

      <Box
        backgroundImage="url('/pattern2.png')"
        backgroundPosition="center"
        backgroundRepeat="repeat"
      >
        <Flex
          justifyContent={"space-between"}
          p={3}
          w={"100%"}
          maxW="1100px"
          margin="0 auto"
          id="top-bar"
        >
          <Flex flex="1" display={"flex"}>
            <Link href="/" _hover={{ textDecoration: "none" }} mr={4}>
              <Image
                maxH={{ base: "100%" }}
                h={{
                  base: `calc(${siteConfig.landingLogoHeight} - 5px)`,
                  sm: siteConfig.landingLogoHeight,
                }}
                my={{ base: "8px", sm: "10px" }}
                src={siteConfig.landingLogoPath}
                alt="Talkio AI"
              />
            </Link>
          </Flex>

          {showTopNav && <NavigationLanding l={l} baseLangCode={baseLangCode} />}
        </Flex>
        <Flex flexDir={"column"} minHeight="calc(100vh - 342px)">
          {children}
        </Flex>
        {isDefaultEnglish && !isFrontpageOrBilling && !isQuiz && !isPartners && (
          <Box id="footer-cta" width={"100%"} backgroundColor="gray.100" py={12}>
            <Section>
              <Flex flexDir={"column"} width={"100%"} alignItems={"center"}>
                <Card
                  direction={{ base: "column", sm: "row" }}
                  overflow="hidden"
                  variant="outline"
                  maxW={"800px"}
                >
                  <Image
                    objectFit="cover"
                    maxW={{ base: "100%", sm: "200px" }}
                    src="/landing-1-thumb.jpeg"
                    alt="Talkio AI"
                  />

                  <Stack>
                    <CardBody>
                      <Heading fontSize={"3xl"}>Talkio AI</Heading>

                      <Text py="2">{l["layout.cta.description"]}</Text>
                    </CardBody>

                    <CardFooter>
                      <Button
                        as="a"
                        href="/"
                        variant="solid"
                        colorScheme="yellow"
                        backgroundColor={"#f6d78b"}
                      >
                        {l["layout.cta.button"]}
                      </Button>
                    </CardFooter>
                  </Stack>
                </Card>
              </Flex>
            </Section>
          </Box>
        )}

        {!isQuiz && (
          <Flex
            id="badge-bar"
            backgroundColor="gray.200"
            py={10}
            justifyContent="center"
            alignItems={{ base: "center", md: "flex-start" }}
            flexDir={{ base: "column", md: "row" }}
          >
            <ProductHuntBadge />

            <Image
              src="/stripe-badge-white.png"
              alt="Stripe Verified Partner"
              height="80px"
              mx={{ base: 0, md: 12 }}
              my={{ base: 5, md: 0 }}
            />

            <Image src="/ssl-secure.png" alt="SSL Secure" height="50px" />
          </Flex>
        )}

        <NewsletterSignup />

        <FooterLanding l={l} baseLangCode={baseLangCode} />
      </Box>
    </Box>
  );
}
